"use client"

import { useRouter } from "next/navigation"
import { useCallback, useEffect, useRef, useState, useTransition } from "react"
import { searchProducts } from "@/actions/search-products"
import type { WooCommerceProduct } from "@/services/woo-commerce-service"

import { SEARCH_DEBOUNCE_MS, SEARCH_MIN_LENGTH } from "../constants"

export type SearchUiState = "idle" | "pending" | "results" | "empty" | "error"

interface SearchRequestGuard {
  invalidate: () => number
  shouldIgnore: (requestId: number) => boolean
}

interface ShouldIgnoreSearchResponseParams {
  requestId: number
  latestRequestId: number
}

interface ResolveSearchUiStateParams {
  hasSearchError: boolean
  hasSearched: boolean
  isSearching: boolean
  productsCount: number
}

export const normalizeSearchQuery = (query: string): string => query.trim()

export const shouldIgnoreSearchResponse = ({
  requestId,
  latestRequestId,
}: ShouldIgnoreSearchResponseParams): boolean => requestId !== latestRequestId

export const resolveSearchUiState = ({
  hasSearchError,
  hasSearched,
  isSearching,
  productsCount,
}: ResolveSearchUiStateParams): SearchUiState => {
  if (isSearching) {
    return "pending"
  }

  if (hasSearchError) {
    return "error"
  }

  if (productsCount > 0) {
    return "results"
  }

  if (hasSearched) {
    return "empty"
  }

  return "idle"
}

export const createSearchRequestGuard = (): SearchRequestGuard => {
  let latestRequestId = 0

  return {
    invalidate: () => {
      latestRequestId += 1
      return latestRequestId
    },
    shouldIgnore: (requestId: number) =>
      shouldIgnoreSearchResponse({ requestId, latestRequestId }),
  }
}

interface UseProductSearchReturn {
  open: boolean
  setOpen: (open: boolean) => void
  searchValue: string
  productsList: WooCommerceProduct[]
  isSearching: boolean
  searchUiState: SearchUiState
  inputRef: React.RefObject<HTMLInputElement | null>
  anchorRef: React.RefObject<HTMLDivElement | null>
  popoverRef: React.RefObject<HTMLDivElement | null>
  handleSearchInput: (value: string) => void
  handleSelectProduct: (permalink: string) => void
  handleClearInput: () => void
  handleSearchClick: () => void
  performSearch: (query: string) => void
}

interface UseProductSearchOptions {
  enableOutsideClose?: boolean
}

export function useProductSearch(
  options: UseProductSearchOptions = {}
): UseProductSearchReturn {
  const { enableOutsideClose = true } = options
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [productsList, setProductsList] = useState<WooCommerceProduct[]>([])
  const [hasSearched, setHasSearched] = useState(false)
  const [hasSearchError, setHasSearchError] = useState(false)
  const [isSearchingState, setIsSearching] = useState(false)
  const [, startTransition] = useTransition()

  const router = useRouter()

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const activeRequestIdRef = useRef<number | null>(null)
  const searchRequestGuardRef = useRef<SearchRequestGuard>(
    createSearchRequestGuard()
  )

  const resetSearchState = useCallback(() => {
    setProductsList([])
    setHasSearched(false)
    setHasSearchError(false)
    setIsSearching(false)
    activeRequestIdRef.current = null
  }, [])

  const performSearch = useCallback(
    (rawQuery: string, scheduledRequestId?: number) => {
      if (scheduledRequestId === undefined && debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }

      const query = normalizeSearchQuery(rawQuery)

      if (query.length < SEARCH_MIN_LENGTH) {
        searchRequestGuardRef.current.invalidate()
        resetSearchState()
        setOpen(false)
        return
      }

      const requestId =
        scheduledRequestId ?? searchRequestGuardRef.current.invalidate()

      activeRequestIdRef.current = requestId
      setIsSearching(true)
      setHasSearchError(false)
      setOpen(true)

      startTransition(async () => {
        try {
          const products = await searchProducts(query)

          if (searchRequestGuardRef.current.shouldIgnore(requestId)) {
            if (activeRequestIdRef.current === requestId) {
              activeRequestIdRef.current = null
              setIsSearching(false)
            }
            return
          }

          activeRequestIdRef.current = null
          setProductsList(products)
          setHasSearched(true)
          setHasSearchError(false)
          setIsSearching(false)
          setOpen(true)
        } catch {
          if (searchRequestGuardRef.current.shouldIgnore(requestId)) {
            if (activeRequestIdRef.current === requestId) {
              activeRequestIdRef.current = null
              setIsSearching(false)
            }
            return
          }

          activeRequestIdRef.current = null
          setProductsList([])
          setHasSearched(true)
          setHasSearchError(true)
          setIsSearching(false)
          setOpen(true)
        }
      })
    },
    [resetSearchState]
  )

  const handleSearchInput = useCallback(
    (value: string) => {
      setSearchValue(value)

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }

      const normalizedValue = normalizeSearchQuery(value)

      if (normalizedValue.length < SEARCH_MIN_LENGTH) {
        searchRequestGuardRef.current.invalidate()
        resetSearchState()
        setOpen(false)
        return
      }

      const requestId = searchRequestGuardRef.current.invalidate()

      setIsSearching(true)
      setHasSearchError(false)
      setOpen(true)

      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null
        performSearch(normalizedValue, requestId)
      }, SEARCH_DEBOUNCE_MS)
    },
    [performSearch, resetSearchState]
  )

  const handleSelectProduct = useCallback(
    (permalink: string) => {
      setOpen(false)
      setSearchValue("")
      searchRequestGuardRef.current.invalidate()
      resetSearchState()

      router.push(permalink)
    },
    [resetSearchState, router]
  )

  const handleClearInput = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }

    searchRequestGuardRef.current.invalidate()
    setSearchValue("")
    resetSearchState()
    setOpen(false)
    inputRef.current?.focus()
  }, [resetSearchState])

  const handleSearchClick = useCallback(() => {
    const normalizedValue = normalizeSearchQuery(searchValue)

    if (normalizedValue.length >= SEARCH_MIN_LENGTH) {
      setSearchValue(normalizedValue)
      performSearch(normalizedValue)
      inputRef.current?.focus()
    }
  }, [searchValue, performSearch])

  const searchUiState = resolveSearchUiState({
    hasSearchError,
    hasSearched,
    isSearching: isSearchingState,
    productsCount: productsList.length,
  })

  const isSearching = searchUiState === "pending"

  // Close popover when focus moves outside input and popover
  useEffect(() => {
    if (!enableOutsideClose) return

    const handleFocusChange = () => {
      const activeElement = document.activeElement as HTMLElement

      if (
        open &&
        activeElement &&
        anchorRef.current &&
        popoverRef.current &&
        !anchorRef.current.contains(activeElement) &&
        !popoverRef.current.contains(activeElement)
      ) {
        setOpen(false)
      }
    }

    document.addEventListener("focusin", handleFocusChange)
    return () => {
      document.removeEventListener("focusin", handleFocusChange)
    }
  }, [enableOutsideClose, open])

  // Close popover when clicking outside
  useEffect(() => {
    if (!enableOutsideClose) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      if (
        open &&
        anchorRef.current &&
        !anchorRef.current.contains(target) &&
        popoverRef.current &&
        !popoverRef.current.contains(target)
      ) {
        setOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [enableOutsideClose, open])

  return {
    open,
    setOpen,
    searchValue,
    productsList,
    isSearching,
    searchUiState,
    inputRef,
    anchorRef,
    popoverRef,
    handleSearchInput,
    handleSelectProduct,
    handleClearInput,
    handleSearchClick,
    performSearch,
  }
}
